/**
 * "Sigma team · Leading Quality of the Complaints" — the line under the
 * door's tab bar that says what the team behind this door is for
 * (kanban 16a22d06).
 *
 * AI/coding check-in 2026-10-19. Scott Alter: "We updated his portal with
 * his definitions, which will centralize once I get those definitions from
 * you [Ravi]." The door keeps NO copy: this line reads the roadmap's
 * team-mandate registry (GET /quality-copilot/team), so the definition Ravi
 * emails (with PJ's amendments) is entered once on /roadmap and shows here.
 *
 * Collapsed, it is the team name and the definition's first line; "What
 * we're for" opens the whole definition and the mandate areas. Open/closed
 * is a per-viewer convenience kept in localStorage (wrapped: private windows
 * and cleared site data may refuse it).
 */
import { useEffect, useState } from 'react';
import { getDoorTeam, type DoorTeamBriefRead } from '../../services/qcpTeam';
import { trackEvent } from '../../services/portalTracking';
import { briefHeadline } from './teamBriefView';
import styles from './QualityTriageShell.module.css';

const OPEN_KEY = 'qcp_team_brief_open';

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // per-viewer convenience only
  }
}

export function TeamBriefLine({ enabled }: { enabled: boolean }) {
  const [brief, setBrief] = useState<DoorTeamBriefRead | null | undefined>(undefined);
  const [open, setOpen] = useState<boolean>(() => readOpen());
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getDoorTeam()
      .then((b) => { if (!cancelled) setBrief(b); })
      .catch(() => { if (!cancelled) setBrief(null); });
    return () => { cancelled = true; };
  }, [enabled]);
  if (!enabled || !brief) return null;
  const headline = briefHeadline(brief);
  const toggle = () => {
    const next = !open;
    trackEvent('action', 'qcp_team_brief_toggle', { open: next, team: brief.team });
    setOpen(next);
    writeOpen(next);
  };
  return (
    <div className={styles.teamBrief} data-testid="team-brief-line" role="note" aria-label={`What ${brief.team} is for`}>
      <div className={styles.teamBriefRow}>
        <strong>{brief.team}</strong>
        {headline && !open && <span className={styles.teamBriefHeadline}>· {headline}</span>}
        {!brief.definition && <span className={styles.teamBriefHeadline}>· no definition written yet</span>}
        {brief.definition && (
          <button
            type="button"
            className={styles.teamBriefToggle}
            aria-expanded={open}
            aria-controls="qcp-team-brief-body"
            onClick={toggle}
          >
            {open ? 'Hide' : "What we're for"}
          </button>
        )}
      </div>
      {open && brief.definition && (
        <div id="qcp-team-brief-body" className={styles.teamBriefBody}>
          <p className={styles.teamBriefDefinition}>{brief.definition}</p>
          {brief.areas.length > 0 && (
            <ul className={styles.teamBriefAreas} aria-label={`Mandate areas of ${brief.team}`}>
              {brief.areas.map((a) => (
                <li key={a.key} title={a.description || undefined}>{a.title}</li>
              ))}
            </ul>
          )}
          <div className={styles.teamBriefSource}>
            {brief.basis ? `${brief.basis} · ` : ''}set by {brief.set_by_email} on {brief.set_at.slice(0, 10)} · edited on the roadmap&apos;s Org chart tab
          </div>
        </div>
      )}
    </div>
  );
}
